/**
 * 業務APIが利用規約の同意を要求した（403 TERMS_ACCEPTANCE_REQUIRED / 503 TERMS_UNAVAILABLE）ことを、
 * axios のインターセプタ（React の外）から画面側へ知らせる。画面側は同意状態を取り直し、
 * 同意画面へ遷移して PubNub 購読・通知処理・業務キャッシュを解除する。
 */
const TERMS_GATE_EVENT = 'line-ban:terms-gate-required'

/** backend が返した機械判定用コード（同意が必要 / 規約を特定できない） */
export type TermsGateCode = 'TERMS_ACCEPTANCE_REQUIRED' | 'TERMS_UNAVAILABLE'

/** 403/503 を受けた要求の主体（その要求の Authorization に使った Firebase UID） */
export type TermsGateEvent = { code: TermsGateCode; uid: string }

export const notifyTermsGateRequired = (
  code: TermsGateCode,
  uid: string,
): void => {
  window.dispatchEvent(
    new CustomEvent<TermsGateEvent>(TERMS_GATE_EVENT, { detail: { code, uid } }),
  )
}

export const subscribeTermsGateRequired = (
  listener: (event: TermsGateEvent) => void,
) => {
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<Partial<TermsGateEvent> | undefined>)
      .detail
    if (typeof detail?.uid !== 'string') return
    listener({
      code:
        detail.code === 'TERMS_UNAVAILABLE'
          ? 'TERMS_UNAVAILABLE'
          : 'TERMS_ACCEPTANCE_REQUIRED',
      uid: detail.uid,
    })
  }
  window.addEventListener(TERMS_GATE_EVENT, handler)
  return () => window.removeEventListener(TERMS_GATE_EVENT, handler)
}

/**
 * 同意の保存後、ログアウトが完了するまでの印（UID単位）。再読込・別タブでも
 * Firebase の利用者が残っているだけで通常画面へ進ませず、ログアウトの完了を案内する。
 */
const SIGNOUT_PENDING_KEY = 'termsConsentSignOutPendingUid'

export const markTermsConsentSignOutPending = (uid: string): void => {
  try {
    localStorage.setItem(SIGNOUT_PENDING_KEY, uid)
  } catch {
    // ストレージが使えない環境でも、この画面内ではログアウト待ちの状態を保持する
  }
}

export const isTermsConsentSignOutPending = (uid: string | undefined): boolean => {
  if (!uid) return false
  try {
    return localStorage.getItem(SIGNOUT_PENDING_KEY) === uid
  } catch {
    return false
  }
}

/** @param uid 指定するとその利用者の印だけを消す（別の利用者の完了処理の印は残す） */
export const clearTermsConsentSignOutPending = (uid?: string): void => {
  try {
    if (uid !== undefined && localStorage.getItem(SIGNOUT_PENDING_KEY) !== uid) {
      return
    }
    localStorage.removeItem(SIGNOUT_PENDING_KEY)
  } catch {
    // noop
  }
}

/**
 * 同意の送信結果が未確認（送信したが応答を受け取れていない）という印（UID・送信した版）。
 * 応答が失われても再読込で本人の同意状態を確かめ、保存済みならログアウトの完了処理へ戻す。
 * この印だけで同意済みとは扱わない。利用者ごとに持ち、別の利用者の印には触れない。
 */
const ACCEPTANCE_UNCONFIRMED_KEY = 'termsAcceptanceUnconfirmed'

type UnconfirmedAcceptance = { uid: string; versionId: number }

const isUnconfirmedAcceptance = (value: unknown): value is UnconfirmedAcceptance =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as UnconfirmedAcceptance).uid === 'string' &&
  typeof (value as UnconfirmedAcceptance).versionId === 'number'

const readUnconfirmedAcceptances = (): UnconfirmedAcceptance[] => {
  try {
    const parsed: unknown = JSON.parse(
      localStorage.getItem(ACCEPTANCE_UNCONFIRMED_KEY) ?? '[]',
    )
    return Array.isArray(parsed) ? parsed.filter(isUnconfirmedAcceptance) : []
  } catch {
    return []
  }
}

const writeUnconfirmedAcceptances = (entries: UnconfirmedAcceptance[]): void => {
  try {
    if (entries.length === 0) {
      localStorage.removeItem(ACCEPTANCE_UNCONFIRMED_KEY)
    } else {
      localStorage.setItem(ACCEPTANCE_UNCONFIRMED_KEY, JSON.stringify(entries))
    }
  } catch {
    // ストレージが使えない環境では、再読込後の確認ができないだけ（同意の保存自体には影響しない）
  }
}

export const markTermsAcceptanceUnconfirmed = (
  uid: string,
  versionId: number,
): void => {
  writeUnconfirmedAcceptances([
    ...readUnconfirmedAcceptances().filter((entry) => entry.uid !== uid),
    { uid, versionId },
  ])
}

export const hasUnconfirmedTermsAcceptance = (uid: string | undefined): boolean =>
  uid !== undefined &&
  readUnconfirmedAcceptances().some((entry) => entry.uid === uid)

/** その利用者の印だけを消す（別の利用者の印には触れない） */
export const clearTermsAcceptanceUnconfirmed = (uid: string): void => {
  const entries = readUnconfirmedAcceptances()
  const rest = entries.filter((entry) => entry.uid !== uid)
  if (rest.length !== entries.length) writeUnconfirmedAcceptances(rest)
}
